import { AwsContractsResponseDto, WorkspaceResponseDto, } from '@lucidlink/dto/web-service-api';
import { arrayParserFromDto, parserFromDto } from '#fetcher';
import { AccountCrypto } from '#sdk/common';
import { isGuestUserAccountDetails } from '#utils/account-details';
import { RequiredCryptoError } from '../workspace-client/crypto';
import { prepareWorkspace } from './create-workspace/prepare-workspace';
export const createWorkspacesManagement = ({ getGlobalFetcher, getWorkspaceFetcher, getSessionManager, loginToWorkspace: loginToWorkspace, }) => {
    return new WorkspacesManagementImpl(getGlobalFetcher, getWorkspaceFetcher, getSessionManager, loginToWorkspace);
};
class WorkspacesManagementImpl {
    getGlobalFetcher;
    getWorkspaceFetcher;
    getSessionManager;
    loginToWorkspace;
    constructor(getGlobalFetcher, getWorkspaceFetcher, getSessionManager, 
    // TODO: remove when frontend is ready
    loginToWorkspace) {
        this.getGlobalFetcher = getGlobalFetcher;
        this.getWorkspaceFetcher = getWorkspaceFetcher;
        this.getSessionManager = getSessionManager;
        this.loginToWorkspace = loginToWorkspace;
    }
    async createWorkspace(params) {
        const accountDetails = await this.getSessionManager().getAccountDetails();
        if (isGuestUserAccountDetails(accountDetails)) {
            throw new RequiredCryptoError();
        }
        const accountCrypto = new AccountCrypto(accountDetails);
        const createWorkspaceDto = await prepareWorkspace(params, await accountCrypto.getAccountKey());
        return this.getGlobalFetcher().post('workspaces', {
            body: createWorkspaceDto,
            parser: parserFromDto(WorkspaceResponseDto),
        });
    }
    async getWorkspace(workspaceId) {
        return this.loginToWorkspace(workspaceId).then(() => {
            return this.getWorkspaceFetcher(workspaceId).get([], {
                parser: parserFromDto(WorkspaceResponseDto),
            });
        });
    }
    async listWorkspaces(params) {
        return this.getGlobalFetcher().get('workspaces', {
            parser: arrayParserFromDto(WorkspaceResponseDto),
            queryParams: {
                includePending: params?.includePending ?? false,
            },
        });
    }
    async deleteWorkspace(workspaceId, params) {
        const queryParams = params ? { code: params.code } : {};
        await this.loginToWorkspace(workspaceId);
        await this.getWorkspaceFetcher(workspaceId).delete([], {
            queryParams,
        });
        await this.getSessionManager().workspaceLogout(workspaceId, workspaceId);
    }
    async sendWorkspaceDeleteVerificationCode(workspaceId) {
        await this.loginToWorkspace(workspaceId).then(async () => {
            await this.getWorkspaceFetcher(workspaceId).post('/delete-verification', {});
        });
    }
    updateWorkspace(workspaceId, params) {
        return this.loginToWorkspace(workspaceId).then(async () => {
            return this.getWorkspaceFetcher(workspaceId).patch([], {
                body: { ...params },
                parser: parserFromDto(WorkspaceResponseDto),
            });
        });
    }
    listAwsContracts() {
        return this.getGlobalFetcher().get(['aws-marketplace', 'contracts'], {
            parser: parserFromDto(AwsContractsResponseDto),
        });
    }
    canClaimAwsContract(params) {
        return this.getGlobalFetcher().post(['clazar', 'contracts', 'availability'], {
            body: { ...params },
        });
    }
    claimAwsContract(params) {
        return this.getGlobalFetcher().post(['clazar', 'contracts'], {
            body: { ...params },
        });
    }
    markWorkspaceOnboardingAsCompleted(workspaceId) {
        return this.loginToWorkspace(workspaceId).then(() => {
            return this.getWorkspaceFetcher(workspaceId).put(['onboarding', 'completed'], {});
        });
    }
    // DEV-ONLY: global-token read of any workspace's audit log via the dev demo endpoint,
    // no workspace link. Response is unvalidated OCSF passthrough.
    queryAuditLogsDemo(workspaceId, filters = {}) {
        return this.getGlobalFetcher().get(['dev', 'audit', 'logs', workspaceId], { queryParams: { ...filters } });
    }
    requestAuditExportDemo(workspaceId, request = {}) {
        return this.getGlobalFetcher().post(['dev', 'audit', 'exports', workspaceId], { body: request });
    }
    getAuditExportDemo(workspaceId, exportId) {
        return this.getGlobalFetcher().get([
            'dev',
            'audit',
            'exports',
            workspaceId,
            exportId,
        ]);
    }
}
